import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { Toaster } from './components/ui/sonner';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { Finance } from './pages/Finance';
import { HRM } from './pages/HRM';
import { Inventory } from './pages/Inventory';
import { Invoicing } from './pages/Invoicing';
import { Outsourcing } from './pages/Outsourcing';
import { OutsourcingWorker } from './pages/OutsourcingWorker';
import { Settings } from './pages/Settings';
import { Reports } from './pages/Reports';
import { Sidebar } from './components/Sidebar';
import { ClientDetail } from './components/finance/ClientDetail';
import { SupplierDetail } from './components/finance/SupplierDetail';
import { canAccessPage, getDefaultRedirect } from './utils/permissions';
import './App.css';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (!canAccessPage(user.role, location.pathname)) {
    return <Navigate to={getDefaultRedirect(user.role)} replace />;
  }

  return (
    <div className="flex">
      {sidebarOpen && <Sidebar onClose={() => setSidebarOpen(false)} />}
      {!sidebarOpen && (
        <button
          onClick={() => setSidebarOpen(true)}
          className="fixed top-4 left-4 z-50 p-2 rounded-md bg-primary text-primary-foreground shadow-lg hover:bg-primary/90"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
        </button>
      )}
      <div className="flex-1 overflow-auto">{children}</div>
    </div>
  );
};

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      <Route path="/" element={user ? <Navigate to={getDefaultRedirect(user.role)} replace /> : <Login />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/finance"
        element={
          <ProtectedRoute>
            <Finance />
          </ProtectedRoute>
        }
      />
      <Route
        path="/finance/client/:id"
        element={
          <ProtectedRoute>
            <ClientDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/finance/supplier/:id"
        element={
          <ProtectedRoute>
            <SupplierDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/hrm/*"
        element={
          <ProtectedRoute>
            <HRM />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory/*"
        element={
          <ProtectedRoute>
            <Inventory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoicing/*"
        element={
          <ProtectedRoute>
            <Invoicing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/outsourcing"
        element={
          <ProtectedRoute>
            <Outsourcing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/outsourcing/worker/:id"
        element={
          <ProtectedRoute>
            <OutsourcingWorker />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <Reports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;